import React, {
	Fragment,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	Menu,
	Transition,
	MenuButton,
	MenuItem,
	MenuItems,
	Popover,
} from '@headlessui/react';
import {
	AdjustmentsHorizontalIcon,
	AdjustmentsVerticalIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronUpDownIcon,
	ChevronUpIcon,
	ExclamationTriangleIcon,
	FunnelIcon,
	MagnifyingGlassIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	useReactTable,
	SortingState,
	getCoreRowModel,
	getSortedRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	flexRender,
} from '@tanstack/react-table';

import useRequired from '../../hooks/useRequired';
import useSearchParams from '../../hooks/useSearchParams';
import useModal from '../../hooks/useModal';
import useExportDownload from '../../hooks/useExportDownload';

import { classNames } from '../helpers/functions';

import {
	TemplateField,
	TemplateReactSelect,
} from '../Attributes/Attributes_old';
import { Button } from '../Button';
import EllipsisDropdown, { EllipsisDropdownItem } from '../EllipsisDropdown';

import SettingsContext from '../../context/SettingsContext';
import history from '../../history';
import useSearchQuery from '../../hooks/useSearchQuery';

const getCommonPinningStyles = (column, isHeader) => {
	const isPinned = column.getIsPinned();
	const isLastLeftPinnedColumn =
		isPinned === 'left' && column.getIsLastColumn('left');
	const isFirstRightPinnedColumn =
		isPinned === 'right' && column.getIsFirstColumn('right');

	return {
		borderRight: isLastLeftPinnedColumn ? '1px solid #f1f1f1' : '',
		left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
		right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
		opacity: isPinned ? 0.95 : 1,
		position: isPinned ? 'sticky' : 'relative',
		minWidth: column.getSize(),
		zIndex: isPinned ? 1 : 0,
		backgroundColor: isPinned ? (isHeader ? '#f9fafbbf' : 'white') : '',
	};
};

/*

    data (object): 
        {
            count: Number,
            rows: Array
        }

    columns (array):
        [{
            header: 'VENDOR',
            accessorKey: 'vendor',
            cell: (info) => {
                return info.getValue()?.companyName || null;
            },
            enableFiltering: true,
            filter: {
                key: 'vendorId',
                placeholder: 'Select a vendor',
                options: vendorOptions,
            },
            enableSorting: false,
            column: {
                removable: Boolean (permanent/removable to the user)
                visible: Boolean (actively visible?  User has the option to hide/show columns via settings)
            }
        }]
*/

const DataTable = ({
	title,
	data,
	columns,
	stateInUrlEnabled = true,
	useSearch = true,
	columnManagementKey,
	onExport,
	exportFileName,
	hasCheckboxes = true,
	manualSorting = true,
	usePagination: usePaginate = true,
	useExport = true,
	onRow,
	actions = [],
	titleActions = [],
	footerActions = [],
	rowActions = [],
	hideColumnHeader = false,
	condensed = false,
	initialState,
	settingsMenuItems = [],
	pinColumn = [],
	className,
	containerClassName,
	rowClassName,
	titleClassName,
	disableScroll,
	disableTitle,
}) => {
	//return <TanStandard />;

	const checkbox = useRef();
	const [checked, setChecked] = useState(false);
	const [indeterminate, setIndeterminate] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);

	const { settings } = useContext(SettingsContext);

	const [isColumnModalOpen, setColumnModalOpen] = useState(false);
	const { setOpen: setColumnManagementOpen } = useModal('manageColumns');
	const { setOpen: setExportModalOpen, setClose: setExportModalClose } =
		useModal('tableCSVExport');

	const { setOpen: onOpenDeleteModal, setClose: onCloseDeleteModal } =
		useModal('deleteModal');

	// Get Filterable Columns
	const filterableColumns = (columns || []).filter(
		(column) => column?.enableFiltering,
	);

	const manageableColumns = (columns || []).filter(
		(column) => column?.column?.removable,
	);

	const showOptionsMenu = useExport || manageableColumns.length;

	const params = useSearchParams();

	const activeFilters = {
		query: params.query,
		page: params.page || 1,
		pageSize: params.pageSize || 10,
		sortBy: params.sortBy,
		sortOrder: params.sortOrder,
	};

	filterableColumns
		.flatMap((o) => o.filter)
		.map((filter) => (activeFilters[filter.key] = params[filter.key]));

	// Show Search Filter
	const [isShowingSearch, setIsShowingSearch] = useState(
		useRequired(activeFilters, ['query']),
	);

	// React Table Sorting
	const [sorting, setSorting] = useState(initialState?.sortBy);

	useEffect(() => {
		if (sorting?.length === 1) {
			const { id, desc } = sorting[0] || {};

			if (manualSorting) {
				useSearchQuery.add(
					{ sortOrder: desc ? 'DESC' : 'ASC', sortBy: id },
					null,
					activeFilters,
				);
			}
		} else if (sorting) {
			if (manualSorting) {
				useSearchQuery.remove(['sortBy', 'sortOrder'], activeFilters);
			}
		}
	}, [sorting]);

	const usePagination = {
		pageIndex: parseInt(activeFilters.page) - 1,
		pageSize: parseInt(activeFilters.pageSize),
		hasNext: (total) => {
			return !!(
				parseInt(activeFilters.page) * parseInt(activeFilters.pageSize) <
				total
			);
		},
		hasPrevious: () => parseInt(activeFilters.page) !== 1,
	};

	const table = useReactTable({
		data: data?.rows || [],
		columns: [
			...(columns?.filter(
				(column) =>
					!column?.column?.removable ||
					(settings?.user &&
						settings?.user[columnManagementKey]?.includes(column?.accessorKey)),
			) || []),
		],
		state: {
			columnVisibility: {},
			sorting,
		},
		onSortingChange: setSorting,
		manualSorting,

		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		isLoading: true,
		defaultColumn: {
			size: 100, //starting column size
			minSize: 50, //enforced during column resizing
			maxSize: 500, //enforced during column resizing
		},
		initialState: {
			columnPinning: {
				left: pinColumn,
			},
		},
	});

	useLayoutEffect(() => {
		const isIndeterminate =
			selectedRows.length > 0 && selectedRows.length < data?.rows?.length;
		setChecked(
			selectedRows.length === data?.rows?.length && data?.rows?.length !== 0,
		);
		setIndeterminate(isIndeterminate);
		if (checkbox?.current) {
			checkbox.current.indeterminate = isIndeterminate;
		}
	}, [selectedRows]);

	function toggleAll() {
		setSelectedRows(
			checked || indeterminate ? [] : data?.rows?.map((row) => row.id),
		);
		setChecked(!checked && !indeterminate);
		setIndeterminate(false);
	}

	const activeFilterCount = Object.keys(activeFilters)
		.filter(
			(key) =>
				!['page', 'pageSize', 'sortBy', 'sortOrder', 'query'].includes(key),
		)
		.filter((key) => activeFilters[key]);

	return (
		<>
			<div className={containerClassName}>
				<div className="">
					<div
						className={classNames(
							disableTitle ? '' : 'flex justify-between mb-5 items-center h-6',
							titleClassName,
						)}
					>
						<div className="font-semibold">
							{title}
							{titleActions?.length
								? titleActions.map((action) => (
										<Button
											type={action.type || 'secondary'}
											size="small"
											onClick={() =>
												typeof action?.onClick === 'function'
													? action.onClick()
													: null
											}
										>
											<span className="text-xxs">{action?.label}</span>
										</Button>
									))
								: null}
						</div>
						<div className="flex items-center justify-end">
							{selectedRows?.length ? (
								<div className="text-xs text-gray-500 flex items-center">
									<span>{`${selectedRows?.length} rows selected`}</span>
									<Button
										type="secondary"
										size="small"
										onClick={() =>
											setExportModalOpen({
												filename: exportFileName,
												columns,
												columnManagementKey,
												selected: selectedRows,
												onExport:
													typeof onExport === 'function'
														? ({ columns, filename }) => {
																onExport(
																	{
																		...activeFilters,
																		export: true,
																		selected: selectedRows,
																		columns,
																	},
																	(response) => {
																		useExportDownload(
																			response,
																			`${filename}.csv`,
																		);
																		setExportModalClose();
																		setSelectedRows([]);
																	},
																);
															}
														: undefined,
											})
										}
									>
										<span className="text-xxs">{`Export Selected`}</span>
									</Button>
								</div>
							) : null}

							{actions?.length
								? actions.map((action) => (
										<Button
											type={action.type || 'primary'}
											size="small"
											onClick={() =>
												typeof action?.onClick === 'function'
													? action.onClick()
													: null
											}
											disabled={action.disabled}
										>
											<span className="text-xxs">{action?.label}</span>
										</Button>
									))
								: null}

							{useSearch && (
								<div className="ml-3 flex items-center">
									<div>
										<Transition
											show={isShowingSearch}
											enter="transition-opacity duration-75"
											enterFrom="opacity-0"
											enterTo="opacity-100"
											leave="transition-opacity duration-150"
											leaveFrom="opacity-100"
											leaveTo="opacity-0"
										>
											<div className="flex items-center">
												<TemplateField
													placeholder="Search..."
													fieldClassName="mt-0"
													onChange={(index, search) =>
														useSearchQuery.add('query', search, activeFilters)
													}
													value={activeFilters?.query || ''}
													autoFocus
												/>
												<XMarkIcon
													className={classNames(
														'w-4 h-4 text-gray-500 ml-2 ',
														activeFilters?.query
															? 'opacity-100 cursor-pointer'
															: 'opacity-0 ',
													)}
													onClick={() =>
														activeFilters?.query
															? useSearchQuery.remove('query', {
																	...activeFilters,
																	page: 1,
																})
															: {}
													}
												/>
											</div>
										</Transition>
									</div>
									<MagnifyingGlassIcon
										className="w-5 h-5 text-gray-500 hover:text-gray-800 cursor-pointer ml-3"
										onClick={() => setIsShowingSearch(!isShowingSearch)}
									/>
								</div>
							)}
							{filterableColumns.flatMap((o) => o.filter)?.length ? (
								<div className="ml-3 flex">
									<Menu as="div" className="relative inline-block text-left">
										<div>
											<Menu.Button
												className={classNames(
													'inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 whitespace-nowrap',
													activeFilterCount?.length ? 'font-semibold' : '',
												)}
											>
												<AdjustmentsHorizontalIcon className="w-4 h-4 text-gray-400" />
												{`Filters${
													activeFilterCount?.length
														? ` (${activeFilterCount?.length})`
														: ''
												}`}
											</Menu.Button>
										</div>

										<Menu.Items
											transition
											className="whitespace-nowrap absolute right-0 z-10 mt-2 w-72 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
										>
											<div className="py-4">
												{filterableColumns
													.flatMap((o) => o.filter)
													?.map((filter) => {
														let options =
															filter.options
																?.filter(Boolean)
																.map((vendor) => ({
																	label: vendor?.name,
																	value: vendor?.id,
																}))
																.sort((a, b) =>
																	a?.label?.localeCompare(b?.label),
																) || [];

														options?.unshift({
															label: 'Unassigned',
															value: 'unassigned',
														});

														return (
															<Menu.Item onClick={(e) => e.preventDefault()}>
																<div className="block px-4 pb-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900">
																	<div className="text-xs mb-2 text-gray-600">
																		{filter?.label}
																	</div>
																	<div className="flex items-center">
																		{filter?.component ? (
																			filter?.component()
																		) : (
																			<>
																				<TemplateReactSelect
																					className="w-64"
																					placeholder={filter?.placeholder}
																					options={options}
																					onChange={(index, option) => {
																						useSearchQuery.add(
																							filter?.key,
																							option?.value,
																							{
																								...activeFilters,
																								page: 1,
																							},
																						);
																					}}
																					value={
																						options?.find(
																							(v) =>
																								v.value ===
																								activeFilters[filter?.key],
																						) || null
																					}
																					disabled={activeFilters[filter?.key]}
																				/>
																				{activeFilters[filter?.key] ? (
																					<XCircleIcon
																						className={classNames(
																							'w-6 h-6 cursor-pointer text-gray-500 mx-2',
																							activeFilters[filter?.key]
																								? 'opacity-100'
																								: 'opacity-0',
																						)}
																						onClick={() => {
																							// Hack: Needed to remove model when removing manufacturer for assets
																							if (
																								filter.key === 'manufacturerId'
																							) {
																								useSearchQuery.remove(
																									['manufacturerId', 'modelId'],
																									activeFilters,
																								);
																							} else {
																								useSearchQuery.remove(
																									filter?.key,
																									activeFilters,
																								);
																							}
																						}}
																					/>
																				) : null}
																			</>
																		)}
																	</div>
																</div>
															</Menu.Item>
														);
													})}
											</div>
										</Menu.Items>
									</Menu>
								</div>
							) : null}
							{showOptionsMenu ? (
								<div className="ml-3 flex">
									<Menu as="div" className="relative inline-block text-left">
										<div>
											<Menu.Button className="flex items-center rounded-full hover:text-gray-800">
												<span className="sr-only">Open options</span>
												<AdjustmentsVerticalIcon className="w-5 h-5 text-gray-500" />
											</Menu.Button>
										</div>

										<Transition
											as={Fragment}
											enter="transition ease-out duration-100"
											enterFrom="transform opacity-0 scale-95"
											enterTo="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leaveFrom="transform opacity-100 scale-100"
											leaveTo="transform opacity-0 scale-95"
										>
											<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<div className="py-1">
													{settingsMenuItems?.length
														? settingsMenuItems?.map((item) => (
																<Menu.Item>
																	{({ active }) => (
																		<div
																			onClick={() =>
																				typeof item?.onClick === 'function'
																					? item?.onClick(item)
																					: null
																			}
																			className={classNames(
																				active
																					? 'bg-gray-100 text-gray-900'
																					: 'text-gray-700',
																				'block px-4 py-2 text-xs cursor-pointer',
																			)}
																		>
																			{item?.name}
																		</div>
																	)}
																</Menu.Item>
															))
														: null}
													{manageableColumns?.length && columnManagementKey ? (
														<Menu.Item>
															{({ active }) => (
																<div
																	onClick={() =>
																		setColumnManagementOpen({
																			columns,
																			columnManagementKey,
																		})
																	}
																	className={classNames(
																		active
																			? 'bg-gray-100 text-gray-900'
																			: 'text-gray-700',
																		'block px-4 py-2 text-xs cursor-pointer',
																	)}
																>
																	Manage Columns
																</div>
															)}
														</Menu.Item>
													) : null}
													{useExport && (
														<Menu.Item>
															{({ active }) => (
																<div
																	onClick={() =>
																		setExportModalOpen({
																			filename: exportFileName,
																			columns,
																			columnManagementKey,
																			onExport:
																				typeof onExport === 'function'
																					? ({ columns, filename }) => {
																							onExport(
																								{
																									...activeFilters,
																									export: true,
																									columns,
																								},
																								(response) => {
																									useExportDownload(
																										response,
																										`${filename}.csv`,
																									);
																									setExportModalClose();
																									setSelectedRows([]);
																								},
																							);
																						}
																					: undefined,
																		})
																	}
																	className={classNames(
																		active
																			? 'bg-gray-100 text-gray-900'
																			: 'text-gray-700',
																		'block px-4 py-2 text-xs cursor-pointer',
																	)}
																>
																	Export to CSV
																</div>
															)}
														</Menu.Item>
													)}
												</div>
											</Menu.Items>
										</Transition>
									</Menu>
								</div>
							) : null}
						</div>
					</div>
					<div
						className={classNames(
							disableTitle ? 'pb-2' : 'py-2',
							'block max-w-full ',
						)}
					>
						<div
							className={classNames(
								disableScroll ? '' : 'overflow-x-scroll overflow-x-hidden',
							)}
						>
							{!disableTitle && <div className="h-2" />}

							<table
								className={classNames(
									'w-full border-separate border-spacing-0 mb-10',
									className,
								)}
							>
								<thead>
									{!hideColumnHeader &&
										table
											.getHeaderGroups()
											.map((headerGroup, headerGroupIdx) => {
												return (
													<tr key={headerGroupIdx}>
														{headerGroup.headers.map((header, headerIdx) => {
															return (
																<th
																	className={classNames(
																		'sticky top-0 z-9 bg-gray-50/75 py-3.5 pl-4 pr-8 sm:pl-6 lg:pl-8 text-left text-xxs font-normal whitespace-nowrap text-gray-500 backdrop-blur backdrop-filter',
																	)}
																	style={{
																		...getCommonPinningStyles(
																			header.column,
																			true,
																		),
																	}}
																	key={headerIdx}
																>
																	{header.isPlaceholder ? null : (
																		<div
																			{...{
																				className: header.column.getCanSort()
																					? 'cursor-pointer select-none flex items-center'
																					: '',
																				onClick:
																					header.column.getToggleSortingHandler(),
																			}}
																		>
																			{flexRender(
																				header.column.columnDef.header,
																				header.getContext(),
																			)}
																			{header.column.getCanSort() &&
																				({
																					asc: (
																						<ChevronUpIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																					),
																					desc: (
																						<ChevronDownIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																					),
																				}[header.column.getIsSorted()] || (
																					<ChevronUpDownIcon className="w-4 h-4 font-bold text-gray-500 ml-1.5" />
																				))}
																		</div>
																	)}
																</th>
															);
														})}
														{(typeof rowActions === 'function'
															? rowActions()
															: rowActions
														)?.length ? (
															<th
																scope="col"
																className={classNames(
																	'sticky top-0 z-0 hidden bg-gray-50/75 px-3 py-3.5 text-left text-xxs font-normal text-gray-500 backdrop-blur backdrop-filter sm:table-cell',
																)}
															></th>
														) : null}
													</tr>
												);
											})}
								</thead>
								<tbody>
									{table.getRowModel().rows.map((row, rowIdx) => {
										return (
											<tr
												className={classNames(
													typeof onRow === 'function' && 'cursor-pointer',
													'group/row',
													rowClassName,
												)}
												key={rowIdx}
												onClick={() =>
													typeof onRow === 'function'
														? onRow(row, rowIdx)
														: null
												}
											>
												{row.getVisibleCells().map((cell, cellIdx) => {
													const { column } = cell;

													return (
														<td
															className={classNames(
																' py-2.5 pl-4 pr-8 sm:pl-6 lg:pl-8  whitespace-nowrap',
																rowIdx !== table.getRowModel().rows.length - 1
																	? 'border-b border-gray-100'
																	: '',
																' whitespace-nowrap',
																cellIdx === 0
																	? `whitespace-${
																			cell?.column?.columnDef?.wrap
																				? 'wrap'
																				: 'nowrap'
																		} text-xs font-medium text-gray-700 `
																	: `whitespace-${
																			cell?.column?.columnDef?.wrap
																				? 'wrap'
																				: 'nowrap'
																		} text-xs text-gray-400`,
																selectedRows.includes(row?.original?.id)
																	? 'bg-indigo-50/50'
																	: '',
															)}
															style={{
																...getCommonPinningStyles(column),
															}}
															key={cellIdx}
														>
															{flexRender(
																column.columnDef?.cell,
																cell?.getContext(),
															)}
														</td>
													);
												})}
												{(typeof rowActions === 'function'
													? rowActions(row?.original)
													: rowActions
												)?.length ? (
													<td
														className={classNames(
															rowIdx !== table.getRowModel().rows.length - 1
																? 'border-b border-gray-100'
																: '',
															condensed ? 'py-2' : 'py-4',
															'whitespace-nowrap px-3 text-xs text-gray-400 flex justify-end',
															selectedRows.includes(row?.original?.id)
																? 'bg-indigo-50/50'
																: '',
														)}
														onClick={(e) => e.stopPropagation()}
													>
														<EllipsisDropdown>
															{(typeof rowActions === 'function'
																? rowActions(row?.original)
																: rowActions
															)?.map((action) => {
																return (
																	<EllipsisDropdownItem
																		className={'text-xs'}
																		onClick={() => {
																			const onAction = () =>
																				typeof action?.onClick
																					? action?.onClick(row)
																					: () => {};
																			if (action?.danger) {
																				onOpenDeleteModal({
																					onConfirm: () => {
																						onAction();
																						onCloseDeleteModal();
																					},
																					confirmText:
																						action?.confirmText || 'Delete',
																					title: action.danger,
																					...(action?.description
																						? {
																								description:
																									action?.description,
																							}
																						: {}),
																				});
																			} else {
																				onAction();
																			}
																		}}
																		danger={action.danger}
																	>
																		{action.name}
																	</EllipsisDropdownItem>
																);
															})}
														</EllipsisDropdown>
													</td>
												) : null}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>

						{footerActions?.length ? (
							<>
								<div className="flex justify-end items-center">
									{footerActions.map((action) => (
										<Button
											type={action.type || 'secondary'}
											size="small"
											onClick={() =>
												typeof action?.onClick === 'function'
													? action.onClick()
													: null
											}
										>
											<span className="text-xxs">{action?.label}</span>
										</Button>
									))}
								</div>
							</>
						) : null}
					</div>

					{!data || data?.rows?.length === 0 ? (
						<div className="flex flex-col items-center py-20">
							<ExclamationTriangleIcon className="w-6 h-6 text-gray-500" />
							<span className="mt-1.5 text-sm text-gray-500">
								No data found
							</span>
						</div>
					) : (
						usePaginate && (
							<nav className="flex items-center justify-end  pt-4 border-gray-200 px-4 sm:px-0 pb-10">
								<div className="text-xs font-medium text-gray-500">
									{`Viewing ${
										parseInt(usePagination.pageIndex) *
											parseInt(usePagination.pageSize) +
										1
									}-${Math.min(
										...[
											parseInt(usePagination.pageIndex) *
												parseInt(usePagination.pageSize) +
												parseInt(usePagination.pageSize),
											data?.total,
										],
									)} of ${data?.total} total`}
								</div>
								<ChevronLeftIcon
									className={classNames(
										'ml-3 h-5 w-5',
										usePagination.hasPrevious(data?.total)
											? ' text-gray-400 hover:text-gray-700 cursor-pointer'
											: 'text-gray-200',
									)}
									aria-hidden="true"
									onClick={() =>
										usePagination.hasPrevious(data?.total)
											? useSearchQuery.add(
													'page',
													parseInt(activeFilters.page) - 1,
													activeFilters,
												)
											: {}
									}
								/>
								<ChevronRightIcon
									className={classNames(
										'ml-3 h-5 w-5',
										usePagination.hasNext(data?.total)
											? ' text-gray-400 hover:text-gray-700 cursor-pointer'
											: 'text-gray-200',
									)}
									aria-hidden="true"
									onClick={() =>
										usePagination.hasNext(data?.total)
											? useSearchQuery.add(
													'page',
													parseInt(activeFilters.page) + 1,
													activeFilters,
												)
											: {}
									}
								/>
							</nav>
						)
					)}
				</div>
			</div>
		</>
	);
};

/*

;

    */

export default DataTable;
